// src/components/WordLookup.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext } from '../context/WordContext';
import './WordLookup.css'; // Ensure this file is in the same folder
import { normalizeWord } from '../utils/wordUtils';
import { ReactComponent as SpeakerIcon } from '../images/icons/speaker.svg';

// Helper function to format definition text with special handling for parenthetical content
const formatDefinition = (text) => {
    if (!text) return '';

    // Find all parenthetical expressions
    const parts = [];
    let currentText = '';
    let inParentheses = 0;
    let parentheticalText = '';

    for (let i = 0; i < text.length; i++) {
        const char = text[i];

        if (char === '(') {
            if (inParentheses === 0) {
                // Save regular text before parentheses
                if (currentText) {
                    parts.push(<span key={`text-${parts.length}`}>{currentText}</span>);
                    currentText = '';
                }
                parentheticalText = '(';
            } else {
                parentheticalText += '(';
            }
            inParentheses++;
        } else if (char === ')' && inParentheses > 0) {
            parentheticalText += ')';
            inParentheses--;

            if (inParentheses === 0) {
                // Save complete parenthetical expression
                parts.push(<span key={`parens-${parts.length}`} className="parenthetical">{parentheticalText}</span>);
                parentheticalText = '';
            }
        } else if (inParentheses > 0) {
            parentheticalText += char;
        } else {
            currentText += char;
        }
    }

    // Add any remaining text
    if (currentText) {
        parts.push(<span key={`text-${parts.length}`}>{currentText}</span>);
    }

    // Add any unclosed parenthetical content
    if (parentheticalText) {
        parts.push(<span key={`parens-${parts.length}`} className="parenthetical">{parentheticalText}</span>);
    }

    return <>{parts}</>;
};

// Verb tenses offered by the "Conjugations" section of the word popup. These mirror
// the Beginner / Intermediate / Advanced groupings used on the Home page so a learner
// sees the same categories in both places. Only languages with a curated list here
// expose the Conjugations section.
const VERB_TENSES_BY_LANGUAGE = {
    Spanish: {
        Beginner: [
            "Present (Presente de Indicativo)",
            "Preterite (Pretérito Indefinido)",
            "Imperfect (Pretérito Imperfecto)",
            "Future Simple (Futuro Simple)",
            "Conditional (Condicional Simple)",
            "Imperative (Imperativo)",
            "Present Perfect (Pretérito Perfecto)"
        ],
        Intermediate: [
            "Subjunctive Present (Presente de Subjuntivo)",
            "Past Perfect (Pretérito Pluscuamperfecto)",
            "Future Perfect (Futuro Perfecto)",
            "Conditional Perfect (Condicional Compuesto)",
            "Imperfect Subjunctive (Pretérito Imperfecto de Subjuntivo)"
        ],
        Advanced: [
            "Past Perfect Subjunctive (Pluscuamperfecto de Subjuntivo)",
            "Future Subjunctive (Futuro de Subjuntivo)"
        ]
    },
    French: {
        Beginner: ["Présent", "Passé Composé", "Imparfait"],
        Intermediate: ["Futur Simple", "Conditionnel Présent", "Subjonctif Présent"],
        Advanced: ["Plus-que-parfait", "Passé Simple", "Passé Antérieur", "Conditionnel Passé", "Futur Antérieur"]
    },
    Italian: {
        Beginner: ["Presente", "Passato Prossimo", "Imperfetto", "Futuro Semplice", "Condizionale Presente"],
        Intermediate: ["Congiuntivo Presente", "Trapassato Prossimo", "Futuro Anteriore", "Condizionale Passato"],
        Advanced: ["Congiuntivo Imperfetto", "Congiuntivo Trapassato"]
    }
};

// Returns true when a fetched definition describes a verb, so the popup can decide
// whether to offer its Conjugations section.
const isVerbDefinition = (def) => {
    if (!def || typeof def !== 'object') return false;
    if (def.parts_of_speech && def.parts_of_speech.verb) return true;
    if (def.verb_info && (def.verb_info.infinitive_form || def.verb_info.tense)) return true;
    if (Array.isArray(def.definitions) &&
        def.definitions.some(d => typeof d === 'string' && /^verb\b/i.test(d.trim()))) {
        return true;
    }
    return false;
};

// ADDED baseLanguage to destructured props
const WordLookup = ({ word, children, language, sentence, onSelect, forDictionary, baseLanguage, lemmatizeSavedWord = false }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    const [definition, setDefinition] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Track the last clicked word to avoid unnecessary API calls
    const [lastClickedWord, setLastClickedWord] = useState(null);
    // Track which accordion section is open (null, 'context', 'grammar', 'conjugation', or 'examples')
    const [openAccordion, setOpenAccordion] = useState(null);
    // --- Conjugation section state ---
    // Which tense's conjugation is currently displayed (null => the tense list view).
    const [activeConjugationTense, setActiveConjugationTense] = useState(null);
    // Cache of already-generated conjugations, keyed by tense name. Once a tense has been
    // generated it is shown instantly on subsequent opens without another LLM request.
    const [conjugations, setConjugations] = useState({});
    const [conjugationLoading, setConjugationLoading] = useState(false);
    const [conjugationError, setConjugationError] = useState(null);
    const [wordGrade, setWordGrade] = useState(1);
    const [wordKnown, setWordKnown] = useState(false);
    // Add a local state to track if word was just saved to force UI update
    const [justSavedWord, setJustSavedWord] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const cachedAudioUrl = useRef(null);
    // Search-bar state (dictionary popup): lets the user look up another word
    // without leaving the popup. Kept separate from the clicked-word definition
    // so the original definition is restored when the search is cleared.
    const [searchTerm, setSearchTerm] = useState('');
    const [searchedFor, setSearchedFor] = useState('');
    const [searchDefinition, setSearchDefinition] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [isSearchMode, setIsSearchMode] = useState(false);
    // Saved/grade/known state for the searched result (kept separate from the clicked
    // word so searching never changes the clicked word's highlight in the text).
    const [searchIsSaved, setSearchIsSaved] = useState(false);
    const [searchGrade, setSearchGrade] = useState(1);
    const [searchKnown, setSearchKnown] = useState(false);
    // Track words whose missing forms we've already tried to backfill this mount,
    // to avoid duplicate POSTs while the context state settles.
    const backfilledFormsRef = useRef(new Set());

    // Use the context instead of state and direct API calls
    const {
        isWordSaved,
        addSavedWord,
        removeSavedWord,
        refreshSavedWords,
        savedWords,
        savedWordsDetails,
        getWordGrade,
        isWordKnown,
        updateWordGrade,
        updateWordKnown,
        getSavedFormMatch,
        backfillSavedWordForms
    } = useWordContext();

    // Normalize the current word for checking
    const normalizedWord = normalizeWord(word);

    // Get current language and base language
    const currentLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish';
    // Fallback to localStorage if baseLanguage isn't passed as a prop
    const userBaseLang = baseLanguage || localStorage.getItem('baseLanguage') || 'English';

    // LEMMA MODE (used by the Card dictionary): when enabled and the fetched definition
    // provides a base form (infinitive for verbs, singular for nouns), use that base form
    // as the key for saved-status checks and for saving. This way any conjugation/plural of
    // a word resolves to the same saved entry once the dictionary popup has loaded.
    const dictionaryLemma = (lemmatizeSavedWord && definition)
        ? String(definition.lemma || definition.verb_info?.infinitive_form || '').trim()
        : '';
    const lemmaKey = dictionaryLemma ? normalizeWord(dictionaryLemma) : '';
    // Inflected surface forms returned by Gemini for the lemma (used both for instant
    // highlighting and to persist alongside the saved word). We always include the exact
    // surface word the user interacted with so that it can be re-highlighted after a
    // refresh even if the model omitted that particular form from its list.
    const dictionaryForms = lemmatizeSavedWord
        ? Array.from(new Set([
            ...((definition && Array.isArray(definition.forms)) ? definition.forms : [])
                .map(f => normalizeWord(String(f)))
                .filter(Boolean),
            normalizedWord
        ].filter(Boolean)))
        : [];
    // INSTANT MATCH: if this surface word is a known inflection of an already-saved word,
    // resolve it to that saved entry immediately (no LLM call needed for highlighting).
    const formMatch = lemmatizeSavedWord ? getSavedFormMatch(normalizedWord, currentLanguage) : null;
    // The key used for saved-status lookups, saving, deleting, grading and known-toggling.
    const matchWord = formMatch || lemmaKey || normalizedWord;

    // Check if the word or its related form is saved
    const checkSavedStatus = useCallback(() => {
        // Check if this word is saved, considering the justSavedWord state
        const exactMatch = isWordSaved(matchWord, currentLanguage);
        if (exactMatch || justSavedWord) {
            logger.debug(`WordLookup - Word "${matchWord}" is saved (exact match or just saved)`);
            return true;
        }

        logger.debug(`WordLookup - Word "${matchWord}" is not saved`);
        return false;
    }, [matchWord, currentLanguage, isWordSaved, justSavedWord]);

    // Check if the word is saved using enhanced check
    const [isSaved, setIsSaved] = useState(false);

    // Update the saved status when data changes
    useEffect(() => {
        const savedStatus = checkSavedStatus();
        logger.debug(`WordLookup - Setting saved status for "${matchWord}" to ${savedStatus}`);
        setIsSaved(savedStatus);
    }, [checkSavedStatus, matchWord, currentLanguage, justSavedWord]);

    // Update local state when the word's saved status changes
    useEffect(() => {
        if (isSaved) {
            setWordGrade(getWordGrade(matchWord, currentLanguage));
            setWordKnown(isWordKnown(matchWord, currentLanguage));
        } else {
            setWordGrade(1);
            setWordKnown(false);
        }
    }, [isSaved, matchWord, currentLanguage, getWordGrade, isWordKnown]);

    // --- Search-result (popup) values ---
    // When the user searches from the popup, the saved-status/save/delete/grading
    // actions must target the searched result instead of the clicked word. Search
    // results always carry a `lemma` (base form) and `forms`, so the same lemma-based
    // logic applies. These values are kept separate from the clicked-word values above
    // so a search never alters the clicked word's highlight in the text.
    const searchLemma = searchDefinition
        ? String(searchDefinition.lemma || '').trim()
        : '';
    const searchNormalizedWord = searchDefinition
        ? normalizeWord(String(searchDefinition.lemma || searchedFor || '').trim())
        : (searchedFor ? normalizeWord(searchedFor) : '');
    const searchLemmaKey = searchLemma ? normalizeWord(searchLemma) : '';
    const searchForms = Array.from(new Set([
        ...((searchDefinition && Array.isArray(searchDefinition.forms)) ? searchDefinition.forms : [])
            .map(f => normalizeWord(String(f)))
            .filter(Boolean),
        searchNormalizedWord
    ].filter(Boolean)));
    const searchFormMatch = searchNormalizedWord ? getSavedFormMatch(searchNormalizedWord, currentLanguage) : null;
    const searchMatchWord = searchFormMatch || searchLemmaKey || searchNormalizedWord;

    // The values the popup UI and the saved-word actions operate on right now.
    const activeMatchWord = isSearchMode ? searchMatchWord : matchWord;
    const activeLemma = isSearchMode ? searchLemma : dictionaryLemma;
    const activeForms = isSearchMode ? searchForms : dictionaryForms;
    const activeIsSaved = isSearchMode ? searchIsSaved : isSaved;
    const activeGrade = isSearchMode ? searchGrade : wordGrade;
    const activeKnown = isSearchMode ? searchKnown : wordKnown;

    // Keep the searched result's saved/grade/known state in sync. This is independent
    // of the clicked word so the clicked word's highlight is never affected by a search.
    useEffect(() => {
        if (!isSearchMode || !searchMatchWord) return;
        const saved = isWordSaved(searchMatchWord, currentLanguage);
        setSearchIsSaved(saved);
        if (saved) {
            setSearchGrade(getWordGrade(searchMatchWord, currentLanguage));
            setSearchKnown(isWordKnown(searchMatchWord, currentLanguage));
        } else {
            setSearchGrade(1);
            setSearchKnown(false);
        }
    }, [isSearchMode, searchMatchWord, currentLanguage, isWordSaved, getWordGrade, isWordKnown, savedWords, savedWordsDetails]);

    // NEW ADDITION: Reset state to avoid stale popups when the word or sentence changes
    useEffect(() => {
        setDefinition(null);
        setLastClickedWord(null);
        setShowPopup(false);
        setError(null);
        setOpenAccordion(null);
        setActiveConjugationTense(null);
        setConjugations({});
        setConjugationLoading(false);
        setConjugationError(null);
        // Clear any in-progress search so a new word starts fresh.
        setSearchTerm('');
        setSearchedFor('');
        setSearchDefinition(null);
        setSearchError(null);
        setIsSearchMode(false);
        setSearchIsSaved(false);
        setSearchGrade(1);
        setSearchKnown(false);
    }, [word, sentence]);

    // BACKFILL: if this word is already saved but its stored inflected forms are missing
    // (legacy saves made before the precomputed-forms feature), persist the forms we just
    // learned from the definition so conjugations/plurals keep highlighting after a refresh.
    useEffect(() => {
        if (!lemmatizeSavedWord || !definition || !isSaved || !matchWord) return;
        const formsFromDef = Array.isArray(definition.forms) ? definition.forms : [];
        const formsToStore = Array.from(new Set([
            ...formsFromDef.map(f => normalizeWord(String(f))).filter(Boolean),
            normalizedWord
        ].filter(Boolean)));
        if (formsToStore.length === 0) return;
        const savedForms = (savedWordsDetails?.[currentLanguage]?.[matchWord]?.forms) || [];
        const savedKeys = new Set(savedForms.map(f => normalizeWord(String(f))));
        const missing = formsToStore.filter(f => !savedKeys.has(normalizeWord(f)));
        if (missing.length === 0) return;
        const guardKey = `${currentLanguage}:${matchWord}`;
        if (backfilledFormsRef.current.has(guardKey)) return;
        backfilledFormsRef.current.add(guardKey);
        backfillSavedWordForms(matchWord, currentLanguage, {
            lemma: dictionaryLemma || matchWord,
            forms: formsToStore
        });
    }, [lemmatizeSavedWord, definition, isSaved, matchWord, currentLanguage, normalizedWord, dictionaryLemma, savedWordsDetails, backfillSavedWordForms]);

    const spanRef = useRef(null);
    const popupRef = useRef(null);

    // Check if Japanese
    const isJapanese = currentLanguage === 'Japanese';

    // Helper function to get userId from multiple sources
    const getUserId = () => {
        // Always try to get the latest userId from the JWT token first
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                // Decode JWT token to get user info
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const decodedToken = JSON.parse(jsonPayload);
                if (decodedToken.sub) {
                    const userId = decodedToken.sub;
                    logger.debug(`WordLookup - Extracted user ID from token: ${userId}`);
                    // Update localStorage with the current userId from the token
                    localStorage.setItem('userId', userId);
                    return userId;
                }
            } catch (err) {
                logger.error("WordLookup - Error decoding token:", err);
            }
        }

        // Fall back to localStorage only if token extraction fails
        const localUserId = localStorage.getItem('userId');
        if (localUserId) {
            logger.debug("WordLookup - Using userId from localStorage (fallback)");
            return localUserId;
        }

        logger.warn("WordLookup - No userId available from token or localStorage");
        return null;
    };

    const fetchDefinition = async () => {
        // If we've already fetched this word and it's being reopened, don't fetch again
        if (lastClickedWord === normalizedWord && definition) {
            logger.debug(`WordLookup - Using cached definition for "${normalizedWord}"`);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Map full language names to language codes
            const languageMapping = {
                'Japanese': 'ja',
                'Spanish': 'es',
                'French': 'fr',
                'Italian': 'it',
                'German': 'de',
                'Portuguese': 'pt',
                'Chinese': 'zh',
                'Russian': 'ru',
                'Arabic': 'ar',
                'Korean': 'ko',
                'Dutch': 'nl',
                'Polish': 'pl',
                'Swedish': 'sv',
                'Turkish': 'tr',
                'Ukrainian': 'uk',
                'Vietnamese': 'vi',
                'Greek': 'el',
                'Hebrew': 'he',
                'Hindi': 'hi',
                'Thai': 'th'
            };

            // Get language code (lowercase) or use full language name if no mapping
            const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();

            // For Japanese, we need to handle the API request differently
            if (isJapanese) {
                logger.debug(`WordLookup - Fetching definition for Japanese word "${word}"`);

                // Use our proxy endpoint for Japanese lookups
                const jishoResponse = await apiClient.get(
                    `/api/jisho?keyword=${encodeURIComponent(word)}`
                );

                if (!jishoResponse.ok) {
                    throw new Error(`Jisho API returned status ${jishoResponse.status}`);
                }

                const jishoData = await jishoResponse.json();
                logger.debug(`WordLookup - Received Jisho data for "${word}":`, jishoData);

                // Format the Jisho response into our expected structure
                if (jishoData.data && jishoData.data.length > 0) {
                    const processedData = {
                        definitions: jishoData.data[0].senses.map(sense =>
                            sense.english_definitions.join('; ')
                        ),
                        reading: jishoData.data[0].japanese[0].reading,
                        jlpt: jishoData.data[0].jlpt.join(', '),
                        isJisho: true
                    };
                    setDefinition(processedData);
                } else {
                    setDefinition({ definitions: [], isJisho: true });
                }
            } else {
                // Check if we have a sentence context
                if (sentence) {
                    logger.debug(`WordLookup - Fetching context definition for "${word}" in sentence: "${sentence}" in ${userBaseLang}`);

                    // Use the contextDefine endpoint when we have a sentence (NOW PASSING BASE LANG)
                    const response = await apiClient.get(
                        `/api/contextDefine?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(sentence)}&lang=${languageCode}&baseLang=${encodeURIComponent(userBaseLang)}`
                    );

                    if (!response.ok) {
                        logger.error(`WordLookup - Context API error status ${response.status} for "${word}"`);
                        throw new Error(`Context definition endpoint returned status ${response.status}`);
                    }

                    const result = await response.json();
                    logger.debug(`WordLookup - Received context definition data for "${word}":`, result);

                    // Validate and process the response
                    processDefinitionResponse(response, result, word);
                } else {
                    logger.debug(`WordLookup - Fetching standard definition for "${word}" in ${userBaseLang}`);

                    // Use the regular define endpoint when we don't have a sentence (NOW PASSING BASE LANG)
                    const response = await apiClient.get(
                        `/api/define?word=${encodeURIComponent(word)}&lang=${languageCode}&baseLang=${encodeURIComponent(userBaseLang)}`
                    );

                    if (!response.ok) {
                        logger.error(`WordLookup - API error status ${response.status} for "${word}"`);
                        throw new Error(`Definition endpoint returned status ${response.status}`);
                    }

                    const result = await response.json();
                    logger.debug(`WordLookup - Received definition data for "${word}":`, result);

                    // Validate and process the response
                    processDefinitionResponse(response, result, word);
                }
            }

            // Update the last clicked word to avoid unnecessary API calls
            setLastClickedWord(normalizedWord);
        } catch (err) {
            logger.error(`WordLookup - Error fetching definition for "${word}":`, err);

            // Create a more user-friendly error message
            const errorMessage = err.message || "Failed to fetch definition";
            setError(errorMessage);

            // Set minimal data to avoid null reference errors
            setDefinition({
                definitions: [`Could not load definition for "${word}". ${errorMessage}`]
            });
        } finally {
            setLoading(false);
        }
    };

    // Search-bar lookup: looks up a term the user typed with NO surrounding context.
    // The endpoint resolves the base/citation form (lemma) and, when the term is in
    // the native language, returns the target-language equivalent. Explanations stay
    // in the native language and context is reported as "N/A".
    const handleSearch = async () => {
        const term = searchTerm.trim();
        if (!term) return;

        // Map full language names to language codes (mirrors fetchDefinition).
        const languageMapping = {
            'Japanese': 'ja', 'Spanish': 'es', 'French': 'fr', 'Italian': 'it',
            'German': 'de', 'Portuguese': 'pt', 'Chinese': 'zh', 'Russian': 'ru',
            'Arabic': 'ar', 'Korean': 'ko', 'Dutch': 'nl', 'Polish': 'pl',
            'Swedish': 'sv', 'Turkish': 'tr', 'Ukrainian': 'uk', 'Vietnamese': 'vi',
            'Greek': 'el', 'Hebrew': 'he', 'Hindi': 'hi', 'Thai': 'th'
        };
        const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();

        setIsSearchMode(true);
        setSearchedFor(term);
        setSearchDefinition(null);
        setSearchError(null);
        setSearchLoading(true);
        setOpenAccordion(null);
        setActiveConjugationTense(null);
        setConjugations({});
        setConjugationLoading(false);
        setConjugationError(null);

        try {
            const response = await apiClient.get(
                `/api/searchDefine?word=${encodeURIComponent(term)}&lang=${languageCode}&baseLang=${encodeURIComponent(userBaseLang)}`
            );

            if (!response.ok) {
                logger.error(`WordLookup - Search API error status ${response.status} for "${term}"`);
                throw new Error(`Search definition endpoint returned status ${response.status}`);
            }

            const result = await response.json();
            logger.debug(`WordLookup - Received search definition data for "${term}":`, result);

            if (!result || typeof result !== 'object') {
                throw new Error('Invalid search response format');
            }

            if (!Array.isArray(result.definitions)) {
                result.definitions = result.definitions ? [String(result.definitions)] : ['No definition available'];
            }
            result.definitions = result.definitions.map(def =>
                typeof def === 'string' ? def : JSON.stringify(def)
            );
            // There is no sentence context for a searched word, so always report N/A.
            if (!result.context_note) {
                result.context_note = 'N/A';
            }

            setSearchDefinition(result);
        } catch (err) {
            logger.error(`WordLookup - Error searching definition for "${term}":`, err);
            const errorMessage = err.message || 'Failed to search definition';
            setSearchError(errorMessage);
            setSearchDefinition({
                definitions: [`Could not load definition for "${term}". ${errorMessage}`]
            });
        } finally {
            setSearchLoading(false);
        }
    };

    // Leave search mode and restore the originally-clicked word's definition.
    const handleBackToWord = () => {
        setIsSearchMode(false);
        setSearchTerm('');
        setSearchedFor('');
        setSearchDefinition(null);
        setSearchError(null);
        setOpenAccordion(null);
        setActiveConjugationTense(null);
        setConjugations({});
        setConjugationLoading(false);
        setConjugationError(null);
        setSearchIsSaved(false);
        setSearchGrade(1);
        setSearchKnown(false);
    };

    // Helper function to process and validate definition response
    const processDefinitionResponse = (response, result, word) => {
        try {
            // Validate the response structure
            if (!result || typeof result !== 'object') {
                logger.error(`WordLookup - Invalid API response format for "${word}":`, result);
                throw new Error('Invalid API response format');
            }

            // Make sure definitions exists and is an array
            if (!Array.isArray(result.definitions)) {
                logger.error(`WordLookup - API response missing definitions array:`, result);
                result.definitions = result.definitions ? [String(result.definitions)] : ['No definition available'];
            }

            // Ensure all definitions are strings
            result.definitions = result.definitions.map(def =>
                typeof def === 'string' ? def : JSON.stringify(def)
            );

            // Check if we have parts_of_speech data
            if (result.parts_of_speech) {
                logger.debug(`WordLookup - Word "${word}" parts of speech:`, result.parts_of_speech);

                // If it's a noun, log additional info
                if (result.parts_of_speech.noun && result.noun_info) {
                    logger.debug(`WordLookup - Noun info for "${word}":`, result.noun_info);
                }

                // If it's a verb, log additional info
                if (result.parts_of_speech.verb && result.verb_info) {
                    logger.debug(`WordLookup - Verb info for "${word}":`, result.verb_info);
                }
            } else {
                logger.debug(`WordLookup - No parts of speech data for "${word}"`);
            }

            setDefinition(result);
        } catch (jsonError) {
            logger.error(`WordLookup - JSON parse error for "${word}":`, jsonError);
            try {
                logger.debug(`WordLookup - Raw response text:`, result);
            } catch (textError) {
                logger.error(`WordLookup - Could not get response text:`, textError);
            }
            setDefinition({
                definitions: [`Error parsing definition data for "${word}"`]
            });
        }
    };

    // Handle click on word
    const handleClick = () => {
        // If it's for dictionary display and we don't have a definition yet, fetch it
        if (forDictionary && (!definition || normalizedWord !== lastClickedWord)) {
            setLastClickedWord(normalizedWord);
            fetchDefinition();
            return;
        }

        const newShowPopup = !showPopup;
        setShowPopup(newShowPopup);

        // Leaving the popup: clear any in-progress search so it reopens on the
        // original word rather than a previous search result.
        if (!newShowPopup) {
            setIsSearchMode(false);
            setSearchTerm('');
            setSearchedFor('');
            setSearchDefinition(null);
            setSearchError(null);
        }

        if (spanRef.current) {
            const rect = spanRef.current.getBoundingClientRect();

            setPopupPosition({
                top: rect.top + window.scrollY - 50, // Position above the word
                left: rect.left + window.scrollX,
            });
        }

        if (newShowPopup) {
            // Check saved status again when popup opens
            const savedStatus = checkSavedStatus();
            setIsSaved(savedStatus);

            // Update grade and known status if word is saved
            if (savedStatus) {
                setWordGrade(getWordGrade(matchWord, currentLanguage));
                setWordKnown(isWordKnown(matchWord, currentLanguage));
            }

            // If we're showing the popup and word hasn't been fetched yet, fetch it
            if (!definition || normalizedWord !== lastClickedWord) {
                setLastClickedWord(normalizedWord);
                fetchDefinition();
            }

            // Call the onSelect callback if provided
            if (onSelect && typeof onSelect === 'function') {
                onSelect(word, sentence);
            }
        }
    };

    // Handle accordion toggling - only one section can be open at a time
    const toggleAccordion = (section) => {
        setOpenAccordion(openAccordion === section ? null : section);
    };

    // Toggle the Conjugations section. Closing it returns the section to its tense-list
    // view so re-opening starts from the list again (generated tenses stay cached).
    const toggleConjugation = () => {
        const nextOpen = openAccordion === 'conjugation' ? null : 'conjugation';
        setOpenAccordion(nextOpen);
        if (nextOpen === null) {
            setActiveConjugationTense(null);
            setConjugationError(null);
        }
    };

    // Generate (or reuse the cached) conjugation for one tense.
    const handleSelectTense = async (tense) => {
        setActiveConjugationTense(tense);
        setConjugationError(null);

        // Already generated this tense -> reuse the cached result; no LLM call.
        if (conjugations[tense]) return;

        setConjugationLoading(true);
        try {
            const languageMapping = { 'Spanish': 'es', 'French': 'fr', 'Italian': 'it' };
            const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();

            // Prefer the verb's base form (infinitive) so any clicked inflection resolves
            // to the same conjugation table.
            const defForConj = isSearchMode ? searchDefinition : definition;
            const verbBase = (defForConj && (defForConj.lemma || (defForConj.verb_info && defForConj.verb_info.infinitive_form)))
                || (isSearchMode ? searchedFor : word);

            logger.debug(`WordLookup - Requesting conjugation for "${verbBase}" (${tense}) in ${currentLanguage}`);

            const response = await apiClient.get(
                `/api/conjugate?word=${encodeURIComponent(verbBase)}&tense=${encodeURIComponent(tense)}&lang=${languageCode}&baseLang=${encodeURIComponent(userBaseLang)}`
            );

            if (!response.ok) {
                throw new Error(`Conjugation endpoint returned status ${response.status}`);
            }

            const result = await response.json();
            setConjugations((prev) => ({ ...prev, [tense]: result }));
        } catch (err) {
            logger.error(`WordLookup - Error fetching conjugation for "${tense}":`, err);
            setConjugationError(err.message || 'Failed to generate conjugation');
        } finally {
            setConjugationLoading(false);
        }
    };

    const handleBackToTenses = () => {
        setActiveConjugationTense(null);
        setConjugationError(null);
    };

    // Render the body of the Conjugations accordion: either the grouped, two-column
    // list of clickable tenses, or the generated conjugation for the selected tense.
    const renderConjugationContent = () => {
        const tenseGroups = VERB_TENSES_BY_LANGUAGE[currentLanguage];
        if (!tenseGroups) return null;

        if (activeConjugationTense) {
            const data = conjugations[activeConjugationTense];
            return (
                <div className="conjugation-panel">
                    <div className="conjugation-panel-header">
                        <button
                            type="button"
                            className="conjugation-back-btn"
                            onClick={handleBackToTenses}
                        >
                            ← Tenses
                        </button>
                        <span className="conjugation-panel-title">{activeConjugationTense}</span>
                    </div>

                    {conjugationLoading && !data && (
                        <p className="conjugation-status">Loading conjugation…</p>
                    )}
                    {conjugationError && (
                        <p className="conjugation-status stories-error-text">Error: {conjugationError}</p>
                    )}

                    {data && (
                        <div className="conjugation-result">
                            {data.infinitive && (
                                <div className="conjugation-infinitive">Base form: {data.infinitive}</div>
                            )}
                            {Array.isArray(data.rows) && data.rows.length > 0 ? (
                                <table className="conjugation-table">
                                    <tbody>
                                        {data.rows.map((row, index) => (
                                            <tr key={`${row.person}-${index}`}>
                                                <td className="conjugation-person">{row.person}</td>
                                                <td className="conjugation-form">{row.form}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                !conjugationLoading && (
                                    <p className="conjugation-status">No forms available.</p>
                                )
                            )}
                            {data.notes && <p className="conjugation-notes">{data.notes}</p>}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="conjugation-tense-list">
                {Object.entries(tenseGroups).map(([level, tenses]) => (
                    <div key={level} className="conjugation-level-group">
                        <div className="conjugation-level-title">{level}</div>
                        <div className="conjugation-tense-grid">
                            {tenses.map((tense) => (
                                <button
                                    type="button"
                                    key={tense}
                                    className={`conjugation-tense-btn${conjugations[tense] ? ' cached' : ''}`}
                                    onClick={() => handleSelectTense(tense)}
                                >
                                    {tense}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                showPopup &&
                spanRef.current &&
                popupRef.current &&
                !spanRef.current.contains(e.target) &&
                !popupRef.current.contains(e.target)
            ) {
                setShowPopup(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPopup]);

    const handleSave = async (e) => {
        e.stopPropagation();
        try {
            // Get userId using helper function
            const userId = getUserId();

            if (!userId) {
                logger.error("WordLookup - No user ID available, cannot save word");
                throw new Error("No user ID available. Please log in again.");
            }

            // In lemma mode, save the base form (infinitive/singular); otherwise the surface
            // word. In search mode this targets the searched result, not the clicked word.
            // Always normalized (removes periods/commas, keeps apostrophes, lowercases).
            const wordToSave = activeMatchWord;

            // Extract definition text and example sentences from the currently displayed
            // result (the searched result in search mode, otherwise the clicked word).
            const defForSave = isSearchMode ? searchDefinition : definition;
            let definitionText = '';
            let exampleSentences = [];
            if (defForSave) {
                // Build definition string from the definitions array
                if (Array.isArray(defForSave.definitions) && defForSave.definitions.length > 0) {
                    definitionText = defForSave.definitions.join('; ');
                }
                // Extract example sentences (Gemini format: [{ sentence: "...", translation: "..." }])
                if (Array.isArray(defForSave.examples) && defForSave.examples.length > 0) {
                    exampleSentences = defForSave.examples;
                }
            }

            logger.debug("=== WORD SAVE DEBUGGING ===");
            logger.debug("Word being saved:", wordToSave);
            logger.debug("Language:", currentLanguage);
            logger.debug("User ID:", userId);
            logger.debug("Definition:", definitionText);
            logger.debug("Examples:", exampleSentences);
            logger.debug("=========================");

            logger.debug(`WordLookup - Saving word: "${wordToSave}" for language: ${currentLanguage} with userId: ${userId}`);

            // Additional check for empty or invalid values
            if (!wordToSave || wordToSave.length === 0) {
                logger.error("WordLookup - Cannot save empty word");
                throw new Error("Cannot save empty word");
            }

            // Send the API request with definition and example sentences
            const result = await apiClient.post('/api/saveUserWord', {
                userId,
                language: currentLanguage,
                item: wordToSave,
                lemma: activeLemma || wordToSave,
                forms: activeForms,
                definition: definitionText,
                exampleSentences: exampleSentences
            });

            // Add debug logging for the response object
            logger.debug("=== SAVE RESPONSE DEBUGGING ===");
            logger.debug("Response object:", result);
            logger.debug("Response type:", typeof result);
            logger.debug("Response has message:", result && result.message ? true : false);
            logger.debug("==============================");

            // Check if result is valid (apiClient.post now returns the parsed JSON directly)
            if (!result || !result.message) {
                logger.error("WordLookup - Failed to save word: Invalid response");
                throw new Error("Failed to save word: Invalid response");
            }

            logger.debug("WordLookup - Word saved successfully:", result.message);

            // Update context with definition and examples
            addSavedWord(wordToSave, currentLanguage, {
                grade: 1,
                known: false,
                lemma: activeLemma || wordToSave,
                forms: activeForms,
                definition: definitionText,
                exampleSentences: exampleSentences
            });

            // Set local state to force UI update immediately. In search mode this must
            // only affect the searched result, never the clicked word's highlight.
            if (isSearchMode) {
                setSearchIsSaved(true);
                setSearchGrade(1);
                setSearchKnown(false);
            } else {
                setJustSavedWord(true);

                // Set initial grade and known state
                setWordGrade(1);
                setWordKnown(false);
            }

            // Force refresh saved words to ensure the list is up to date
            refreshSavedWords();

            // Dispatch an event to notify that word data has been updated
            // This will trigger the WordCountContext to update its counts
            window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
        } catch (error) {
            logger.error("WordLookup - Error saving word:", error);
            logger.error("WordLookup - Error details:", {
                message: error.message,
                name: error.name,
                stack: error.stack
            });
            if (isSearchMode) setSearchError("Failed to save word. Please try again.");
            else setError("Failed to save word. Please try again.");
        }
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        try {
            const userId = getUserId(); // Use helper function

            if (!userId) {
                logger.error("WordLookup - No user ID available, cannot remove word");
                throw new Error("No user ID available. Please log in again.");
            }

            // In lemma mode, remove the base form (infinitive/singular) that was saved.
            // In search mode this targets the searched result, not the clicked word.
            const wordToDelete = activeMatchWord;

            logger.debug(`WordLookup - Removing saved word: "${wordToDelete}" for language: ${currentLanguage}`);

            const result = await apiClient.post('/api/removeUserWord', {
                userId,
                language: currentLanguage,
                item: wordToDelete
            });

            // Check if result is valid (apiClient.post now returns the parsed JSON directly)
            if (!result || !result.message) {
                logger.error("WordLookup - Failed to remove word: Invalid response");
                throw new Error("Failed to remove word: Invalid response");
            }

            logger.debug("WordLookup - Word removed successfully:", result.message);

            // Update context
            removeSavedWord(wordToDelete, currentLanguage);

            // Reset the just-saved / saved state for the affected word.
            setJustSavedWord(false);
            if (isSearchMode) setSearchIsSaved(false);

            // Force refresh saved words
            refreshSavedWords();

            // Dispatch an event to notify that word data has been updated
            // This will trigger the WordCountContext to update its counts
            window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
        } catch (error) {
            logger.error("WordLookup - Error removing word:", error);
            if (isSearchMode) setSearchError("Failed to remove word. Please try again.");
            else setError("Failed to remove word. Please try again.");
        }
    };

    const handleGradeChange = async (e) => {
        e.stopPropagation();
        const newGrade = parseInt(e.target.value, 10);
        if (isSearchMode) setSearchGrade(newGrade);
        else setWordGrade(newGrade);

        if (activeIsSaved) {
            try {
                // updateWordGrade handles the API call directly
                const success = await updateWordGrade(activeMatchWord, currentLanguage, newGrade);
                if (!success) {
                    logger.error("WordLookup - Failed to update word grade");
                    setError("Failed to update word grade. Please try again.");
                }
            } catch (error) {
                logger.error("WordLookup - Error updating word grade:", error);
                setError("Failed to update word grade. Please try again.");
            }
        }
    };

    const handleKnownChange = async (e) => {
        e.stopPropagation();
        const isKnown = e.target.checked;
        if (isSearchMode) setSearchKnown(isKnown);
        else setWordKnown(isKnown);

        if (activeIsSaved) {
            try {
                // updateWordKnown handles the API call directly
                const success = await updateWordKnown(activeMatchWord, currentLanguage, isKnown);
                if (!success) {
                    logger.error("WordLookup - Failed to update word known status");
                    setError("Failed to update word status. Please try again.");
                }
            } catch (error) {
                logger.error("WordLookup - Error updating word known status:", error);
                setError("Failed to update word status. Please try again.");
            }
        }
    };

    const handlePlayWord = async (wordToPlay) => {
        if (isPlaying) return;
        const wordForTTS = wordToPlay || word;
        
        // Check cache first
        if (cachedAudioUrl.current) {
            try {
                setIsPlaying(true);
                const audio = new Audio(cachedAudioUrl.current);
                audio.crossOrigin = 'anonymous';
                audio.onended = () => setIsPlaying(false);
                audio.onerror = () => {
                    cachedAudioUrl.current = null;
                    setIsPlaying(false);
                };
                await audio.play();
                return;
            } catch (e) {
                cachedAudioUrl.current = null;
                setIsPlaying(false);
            }
        }

        try {
            setIsPlaying(true);
            const payload = {
                text: wordForTTS,
                learningLanguage: currentLanguage,
            };
            if (currentLanguage === 'Spanish') {
                payload.spanishRegion = 'Latin America';
            }
            const data = await apiClient.post('/api/tts', payload);
            const url = data.audio;
            cachedAudioUrl.current = url;
            const audio = new Audio(url);
            audio.crossOrigin = 'anonymous';
            audio.onended = () => setIsPlaying(false);
            audio.onerror = () => {
                cachedAudioUrl.current = null;
                setIsPlaying(false);
            };
            await audio.play();
        } catch (error) {
            logger.error('WordLookup - Error playing TTS:', error);
            cachedAudioUrl.current = null;
            setIsPlaying(false);
        }
    };

    const renderJapaneseDefinition = (activeWord, activeDefinition) => {
        // Shadow the outer word/definition with the values currently being displayed.
        const word = activeWord;
        const definition = activeDefinition;
        return (
            <div>
                <div className="stories-word-heading-row">
                    <strong>{word}</strong>
                    <button
                        className="stories-speaker-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePlayWord(word);
                        }}
                        disabled={isPlaying}
                        title="Play pronunciation"
                    >
                        <SpeakerIcon className="stories-speaker-icon" />
                    </button>
                </div>
                <hr className="definition-separator" />
                <div className="definition-content">
                    {definition.reading && <p className="reading">{definition.reading}</p>}
                    {definition.jlpt && <p className="jlpt">JLPT: {definition.jlpt}</p>}
                    <ul>
                        {definition.definitions.length > 0 ?
                            definition.definitions.map((def, index) => (
                                <li key={index}>{formatDefinition(def)}</li>
                            )) :
                            <li>No definition found.</li>
                        }
                    </ul>
                </div>
            </div>
        );
    };

    const renderStandardDefinition = (activeWord, activeDefinition, useLemma) => {
        // Shadow the outer word/definition with the values currently being displayed
        // (either the normally-clicked word or the search-bar result).
        const word = activeWord;
        const definition = activeDefinition;
        // First check if we have valid data
        if (!definition) {
            return (
                <div>
                    <strong>{word}</strong>
                    <hr className="definition-separator" />
                    <p>No definition data available.</p>
                </div>
            );
        }

        logger.debug(`WordLookup - Rendering definition for "${word}":`, definition);

        // Group definitions by word type if they follow the format "type: definition"
        const groupedDefinitions = {};

        if (definition.definitions && definition.definitions.length > 0) {
            // Ensure all definitions are strings
            const stringDefinitions = definition.definitions.map(def =>
                typeof def === 'string' ? def : JSON.stringify(def)
            );

            stringDefinitions.forEach(def => {
                // Check if the definition includes a word type (e.g., "noun: definition")
                const match = def.match(/^([\w\s]+):(.*)/i);

                if (match) {
                    const wordType = match[1].trim().toLowerCase();
                    let definitionContent = match[2].trim();

                    // More intelligent comma-based split that respects parentheses
                    // This prevents splitting "new (plural, masculine)" into separate definitions
                    const definitions = [];
                    let currentDef = '';
                    let inParentheses = 0;

                    // Go through character by character
                    for (let i = 0; i < definitionContent.length; i++) {
                        const char = definitionContent[i];

                        // Track parentheses
                        if (char === '(') inParentheses++;
                        else if (char === ')') inParentheses--;

                        // If comma found and not inside parentheses, end definition
                        if (char === ',' && inParentheses === 0) {
                            if (currentDef.trim()) {
                                definitions.push(currentDef.trim());
                            }
                            currentDef = '';
                        } else {
                            currentDef += char;
                        }
                    }

                    // Add the last definition if there is one
                    if (currentDef.trim()) {
                        definitions.push(currentDef.trim());
                    }

                    if (!groupedDefinitions[wordType]) {
                        groupedDefinitions[wordType] = [];
                    }

                    // Add each definition individually
                    groupedDefinitions[wordType].push(...definitions);
                } else {
                    // If no word type is found, add to "other" category
                    if (!groupedDefinitions.other) {
                        groupedDefinitions.other = [];
                    }
                    groupedDefinitions.other.push(def);
                }
            });
        } else {
            // If no definitions array, create a default "other" category
            groupedDefinitions.other = ["No definition available"];
        }

        // Order of word types to display
        const wordTypeOrder = [
            'noun', 'masculine noun', 'feminine noun', 'plural noun',
            'verb', 'transitive verb', 'intransitive verb', 'reflexive verb',
            'adjective', 'adverb', 'pronoun',
            'preposition', 'conjunction', 'interjection', 'article', 'determiner', 'other'
        ];

        // Sort word types by the predefined order
        const sortedWordTypes = Object.keys(groupedDefinitions).sort((a, b) => {
            const indexA = wordTypeOrder.indexOf(a);
            const indexB = wordTypeOrder.indexOf(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        // Clean up the display word (remove periods and commas but keep apostrophes)
        const displayWord = word.replace(/[.,]/g, '');

        // In lemma mode, show the base form (infinitive/singular) as the headword so the
        // user sees the exact form that will be saved. The lemma is taken from the
        // definition currently being displayed (clicked word in lemma mode, or search result).
        const localLemma = (useLemma && definition)
            ? String(definition.lemma || definition.verb_info?.infinitive_form || '').trim()
            : '';
        const headword = localLemma ? localLemma : displayWord;

        // Check if the word is an infinitive verb
        // Normalize both the current word and the infinitive form for comparison
        const normalizedDisplayWord = normalizeWord(displayWord);
        let isInfinitiveVerb = false;

        if (definition.verb_info && definition.verb_info.infinitive_form) {
            const infinitiveForm = typeof definition.verb_info.infinitive_form === 'string'
                ? definition.verb_info.infinitive_form
                : String(definition.verb_info.infinitive_form);

            // Multiple ways to check for a match
            const normalizedInfinitive = normalizeWord(infinitiveForm);

            // Direct match
            const directMatch = (normalizedDisplayWord === normalizedInfinitive);

            // Check if the current word ends with the infinitive marker (e.g., "er", "ir", "re" in French)
            const hasInfinitiveEnding = /er$|ir$|re$/.test(normalizedDisplayWord);

            // If the word itself has an infinitive ending AND it matches the provided infinitive form
            isInfinitiveVerb = directMatch ||
                (hasInfinitiveEnding &&
                    (normalizedDisplayWord === normalizedInfinitive ||
                        normalizedDisplayWord.includes(normalizedInfinitive) ||
                        normalizedInfinitive.includes(normalizedDisplayWord)));

            if (isInfinitiveVerb) {
                logger.debug(`WordLookup - Word "${normalizedDisplayWord}" is detected as an infinitive verb matching "${normalizedInfinitive}"`);
            }
        }

        return (
            <div>
                <div className="stories-word-heading-row">
                    <strong>{headword}</strong>
                    <button
                        className="stories-speaker-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePlayWord(headword);
                        }}
                        disabled={isPlaying}
                        title="Play pronunciation"
                    >
                        <SpeakerIcon className="stories-speaker-icon" />
                    </button>
                </div>
                {headword !== displayWord && (
                    <div
                        className="stories-lemma-subtitle"
                        style={{ fontSize: '0.85em', opacity: 0.75, marginTop: '2px' }}
                    >
                        Base form of “{displayWord}”
                    </div>
                )}
                {definition.direct_translation && (
                    <div className="stories-direct-translation">
                        {definition.direct_translation}
                    </div>
                )}
                {Array.isArray(definition.synonyms) && definition.synonyms.filter(Boolean).length > 0 && (
                    <div className="stories-synonyms">
                        <span className="stories-synonyms-label">Synonyms:</span>{' '}
                        {definition.synonyms.filter(Boolean).join(', ')}
                    </div>
                )}
                <hr className="definition-separator" />

                <div className="definition-content">
                    {sortedWordTypes.length > 0 ? (
                        // Display each word type and its definitions
                        sortedWordTypes.map(wordType => (
                            <div key={wordType} className="word-type-section">
                                <h4 className="word-type">{wordType !== 'other' ? wordType : ''}</h4>
                                <ul>
                                    {groupedDefinitions[wordType].map((def, index) => (
                                        <li key={index}>
                                            {typeof def === 'string' ? (
                                                // Format the definition to handle parenthetical content specially
                                                formatDefinition(def)
                                            ) : (
                                                JSON.stringify(def)
                                            )}
                                        </li>
                                    ))}
                                </ul>

                                {/* Add verb information if available and this is a verb section */}
                                {wordType.includes('verb') && definition.verb_info && (
                                    <div className="verb-info">
                                        {isInfinitiveVerb ? (
                                            // Special display for infinitive verbs
                                            <>
                                                <p><strong>Verb form:</strong> Infinitive</p>
                                                {definition.verb_info.infinitive_form && (
                                                    <p>
                                                        <strong>Infinitive:</strong> {typeof definition.verb_info.infinitive_form === 'string' ?
                                                            definition.verb_info.infinitive_form : JSON.stringify(definition.verb_info.infinitive_form)}
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            // Regular display for conjugated verbs
                                            <>
                                                {definition.verb_info.person && (
                                                    <p>
                                                        <strong>Person:</strong> {typeof definition.verb_info.person === 'string' ?
                                                            definition.verb_info.person : JSON.stringify(definition.verb_info.person)}
                                                    </p>
                                                )}
                                                {definition.verb_info.tense && (
                                                    <p>
                                                        <strong>Tense:</strong> {typeof definition.verb_info.tense === 'string' ?
                                                            definition.verb_info.tense : JSON.stringify(definition.verb_info.tense)}
                                                    </p>
                                                )}
                                                {definition.verb_info.infinitive_form && (
                                                    <p>
                                                        <strong>Infinitive form:</strong> {typeof definition.verb_info.infinitive_form === 'string' ?
                                                            definition.verb_info.infinitive_form : JSON.stringify(definition.verb_info.infinitive_form)}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <ul>
                            <li>No definition found.</li>
                        </ul>
                    )}
                </div>
            </div>
        );
    };

    const renderPopupContent = () => {
        // In search mode the popup shows the result for the term the user typed
        // (which may be written in either the learning or the native language).
        // Otherwise we fall back to the normally-clicked word.
        const activeWord = isSearchMode ? searchedFor : word;
        const activeDefinition = isSearchMode ? searchDefinition : definition;
        const activeLoading = isSearchMode ? searchLoading : loading;
        const activeError = isSearchMode ? searchError : error;

        let content;

        if (activeLoading) {
            content = <p>Loading...</p>;
        } else if (activeDefinition) {
            // Search results always come back in the standard (non-Jisho) shape, so
            // use the standard renderer even for Japanese. Lemma resolution applies in
            // search mode so inflected searches show their base form as the headword.
            content = (isJapanese && !isSearchMode) ?
                renderJapaneseDefinition(activeWord, activeDefinition) :
                renderStandardDefinition(activeWord, activeDefinition, isSearchMode ? true : lemmatizeSavedWord);
        } else if (activeError) {
            content = <p className="stories-error-text">Error: {activeError}</p>;
        } else {
            content = <p>No definition found.</p>;
        }

        // Only show grading UI if the word is saved (searched word in search mode).
        const renderGradingUI = () => {
            if (!activeIsSaved) return null;

            return (
                <div className="stories-word-grading">
                    <div className="difficulty-scale-stories">
                        <span className="stories-difficulty-hard">Hard</span>
                        <span className="stories-difficulty-easy">Easy</span>
                    </div>
                    <div className="stories-radio-group">
                        {[1, 2, 3, 4, 5].map(grade => (
                            <div
                                key={grade}
                                className={`stories-radio-option stories-radio-grade-${grade} ${activeGrade === grade ? 'selected' : ''}`}
                            >
                                <input
                                    type="radio"
                                    id={`grade-${grade}`}
                                    name="word-grade"
                                    value={grade}
                                    checked={activeGrade === grade}
                                    className={`stories-radio-grade-${grade}`}
                                    onChange={handleGradeChange}
                                />
                                <label htmlFor={`grade-${grade}`}>{grade}</label>
                            </div>
                        ))}
                    </div>

                    <div className="stories-known-checkbox">
                        <input
                            type="checkbox"
                            id="word-known"
                            checked={activeKnown}
                            onChange={handleKnownChange}
                        />
                        <div>
                            <label htmlFor="word-known" className="stories-known-checkbox-label">
                                I know this word now.
                            </label>
                            <div className="stories-known-checkbox-description">
                                Check this box to remove the highlight.
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div>
                {/* Search bar (popup only): look up another word without leaving the popup */}
                {!forDictionary && (
                    <div className="stories-dictionary-search">
                        <input
                            type="text"
                            className="stories-dictionary-search-input"
                            placeholder="Search another word..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                        {isSearchMode && (
                            <button
                                type="button"
                                className="stories-dictionary-search-back"
                                onClick={handleBackToWord}
                                title="Back to the original word"
                            >
                                ← Back
                            </button>
                        )}
                    </div>
                )}

                {content}

                {/* Context accordion */}
                {activeDefinition && activeDefinition.context_note && (
                    <div className="stories-definition-accordion">
                        <div
                            className="stories-definition-accordion-header"
                            onClick={() => toggleAccordion('context')}
                        >
                            <h3 className="stories-definition-accordion-title">Context</h3>
                            <span className="stories-definition-accordion-icon">
                                {openAccordion === 'context' ? '▲' : '▼'}
                            </span>
                        </div>
                        {openAccordion === 'context' && (
                            <div className="stories-definition-accordion-content">
                                <p className="context-note">{activeDefinition.context_note}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Grammar accordion */}
                {activeDefinition && activeDefinition.grammar_note && (
                    <div className="stories-definition-accordion">
                        <div
                            className="stories-definition-accordion-header"
                            onClick={() => toggleAccordion('grammar')}
                        >
                            <h3 className="stories-definition-accordion-title">Grammar</h3>
                            <span className="stories-definition-accordion-icon">
                                {openAccordion === 'grammar' ? '▲' : '▼'}
                            </span>
                        </div>
                        {openAccordion === 'grammar' && (
                            <div className="stories-definition-accordion-content">
                                <p className="grammar-note">{activeDefinition.grammar_note}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Conjugations accordion - shown for verbs that have a curated tense list.
                    Lists tenses by level in two columns; selecting one reveals its
                    conjugation (cached, so re-opening never re-queries the model). */}
                {activeDefinition && isVerbDefinition(activeDefinition) && VERB_TENSES_BY_LANGUAGE[currentLanguage] && (
                    <div className="stories-definition-accordion">
                        <div
                            className="stories-definition-accordion-header"
                            onClick={toggleConjugation}
                        >
                            <h3 className="stories-definition-accordion-title">Conjugations</h3>
                            <span className="stories-definition-accordion-icon">
                                {openAccordion === 'conjugation' ? '▲' : '▼'}
                            </span>
                        </div>
                        {openAccordion === 'conjugation' && (
                            <div className="stories-definition-accordion-content">
                                {renderConjugationContent()}
                            </div>
                        )}
                    </div>
                )}

                {/* Example Sentences accordion */}
                {activeDefinition && activeDefinition.examples && Array.isArray(activeDefinition.examples) && activeDefinition.examples.length > 0 && (
                    <div className="stories-definition-accordion">
                        <div
                            className="stories-definition-accordion-header"
                            onClick={() => toggleAccordion('examples')}
                        >
                            <h3 className="stories-definition-accordion-title">Example Sentences</h3>
                            <span className="stories-definition-accordion-icon">
                                {openAccordion === 'examples' ? '▲' : '▼'}
                            </span>
                        </div>
                        {openAccordion === 'examples' && (
                            <div className="stories-definition-accordion-content">
                                {activeDefinition.examples.map((example, index) => (
                                    <div key={index} style={{ marginBottom: '12px' }}>
                                        <p style={{
                                            color: '#ffffffff',
                                            fontWeight: '500',
                                            marginBottom: '4px',
                                            fontSize: '15px'
                                        }}>
                                            {example.sentence}
                                        </p>
                                        <p style={{
                                            color: '#ddddddff',
                                            fontStyle: 'italic',
                                            marginTop: '0',
                                            fontSize: '14px'
                                        }}>
                                            {example.translation}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}


                <div className="stories-WordLookup-popup-buttons">
                    {!activeIsSaved ? (
                        <button onClick={handleSave} className="save-word-btn">Save Word</button>
                    ) : (
                        <button onClick={handleDelete} className="delete-word-btn">Delete Word</button>
                    )}
                </div>

                {renderGradingUI()}

                {activeError && <div className="stories-error-text">{activeError}</div>}
            </div>
        );
    };

    // Determine the CSS class based on saved status and grade
    const getWordClass = () => {
        // Check using both the context-based isSaved and the local justSavedWord state
        const actuallyIsSaved = isSaved || justSavedWord;
        if (!actuallyIsSaved) return 'clickable-word';

        let className = 'clickable-word saved-word';

        // Add grade-specific class
        className += ` grade-${wordGrade}`;

        // If word is marked as known, don't apply saved-word styling
        if (wordKnown) {
            className = 'clickable-word';
        }

        logger.debug(`WordLookup - Returning class for "${normalizedWord}": ${className}`);
        return className;
    };

    // For dictionary panel display, auto-fetch definition and show without a popup
    useEffect(() => {
        if (forDictionary && (!definition || normalizedWord !== lastClickedWord)) {
            fetchDefinition();
            setLastClickedWord(normalizedWord);
        }
    }, [forDictionary, word, definition, normalizedWord, lastClickedWord]);

    // If this is for the dictionary panel, render the definition content directly
    if (forDictionary) {
        return (
            <div className="dictionary-word-definition">
                {renderPopupContent()}
            </div>
        );
    }

    return (
        <span
            ref={spanRef}
            className={getWordClass()}
            onClick={handleClick}
        >
            {children || word}
            {showPopup &&
                ReactDOM.createPortal(
                    <div
                        ref={popupRef}
                        className="stories-definition-popup"
                        style={{
                            top: `${popupPosition.top}px`,
                            left: `${popupPosition.left}px`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="stories-word-definition-container">
                            {renderPopupContent()}
                        </div>
                    </div>,
                    document.body
                )}
        </span>
    );
};

export default WordLookup;